function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Decimal from 'decimal.js';
import produce from 'immer';
import { serviceModel } from '..';
import { DiscountModel } from "../DiscountModel";
import * as serviceBillingModel from "../service-billing";
import { makeBillUpfrontState } from "../service-billing/makers";
import { RecurrencePeriodTypes } from "../service-billing/model";
import * as serviceCostModel from "../service-cost";
import { makeAmendedStatus, makeApprovedStatus, makeAwaitingApprovalStatus, makeCanceledStatus, makeCompletedStatus, makeTerminatedStatus, makeChanges, makeMandatoryParticipation, makeOptionalParticipation, makeOneTimeManualStatus, makeOnApprovalStatus, makeOneTimeInStagesStatus } from "./makers";
import { ToggledField, toggleNewOngoingBilling } from "./newOngoingBilling";
import { effectiveDate, isAmended, isAwaitingApproval, isCompleted, changes, startBillingAt } from "./selectors";
export function setBillingTrigger(service, billingTrigger) {
  return _objectSpread(_objectSpread({}, service), {}, {
    billing: billingTrigger
  });
}
export function setBillUpfront(service, billUpfront) {
  return produce(service, s => {
    if (serviceBillingModel.isOngoing(s.billing) && billUpfront !== s.billing.isBilledUpfront) {
      if (isAwaitingApproval(s.status)) {
        return toggleNewOngoingBilling(service, ToggledField.IsBilledUpfront);
      } else {
        s.billing.isBilledUpfront = billUpfront;
      }
    }
  });
}
export function setSkipBillingOnAcceptance(service, skipBillingOnAcceptance, featureFlags) {
  return produce(service, s => {
    if (featureFlags.START_BILLING_AT && startBillingAt(service) && !skipBillingOnAcceptance) {
      skipBillingOnAcceptance = true;
    }
    if (serviceBillingModel.isOngoing(s.billing) && skipBillingOnAcceptance !== s.billing.billUpfrontState.skipBillingOnAcceptance) {
      if (isAwaitingApproval(s.status)) {
        return toggleNewOngoingBilling(service, ToggledField.BillOnAcceptance);
      } else {
        s.billing.billUpfrontState = makeBillUpfrontState(skipBillingOnAcceptance);
      }
    }
  });
}
export function setBillingDayOfWeek(service, billingDayOfWeek) {
  const newBilling = serviceBillingModel.setBillingDayOfWeek(service.billing, billingDayOfWeek);
  return newBilling !== service.billing ? _objectSpread(_objectSpread({}, service), {}, {
    billing: newBilling
  }) : service;
}
export function setBillingDayOfMonth(service, billingDayOfMonth) {
  const newBilling = serviceBillingModel.setBillingDayOfMonth(service.billing, billingDayOfMonth);
  return newBilling !== service.billing ? _objectSpread(_objectSpread({}, service), {}, {
    billing: newBilling
  }) : service;
}
export function setBillingDayOfYear(service, billingDayOfYear) {
  const newBilling = serviceBillingModel.setBillingDayOfYear(service.billing, billingDayOfYear);
  return newBilling !== service.billing ? _objectSpread(_objectSpread({}, service), {}, {
    billing: newBilling
  }) : service;
}
export function setMaxCharges(service, enabled, limit) {
  let chargesLeft = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : limit;
  const newBilling = serviceBillingModel.setMaxCharges(service.billing, enabled, limit, chargesLeft);
  return newBilling !== service.billing ? _objectSpread(_objectSpread({}, service), {}, {
    billing: newBilling
  }) : service;
}
export function setIntegratedClass(service, integratedClass) {
  return _objectSpread(_objectSpread({}, service), {}, {
    integrations: service.integrations.setQboIntegratedClass(integratedClass)
  });
}
export function deleteMaxCharges(service) {
  const newBilling = serviceBillingModel.deleteMaxCharges(service.billing);
  return newBilling !== service.billing ? _objectSpread(_objectSpread({}, service), {}, {
    billing: newBilling
  }) : service;
}
export function setPaused(service, isPaused) {
  return produce(service, s => {
    if (serviceBillingModel.isOngoing(s.billing) && isPaused !== s.billing.isPaused) {
      if (isAwaitingApproval(s.status)) {
        return toggleNewOngoingBilling(service, ToggledField.IsPaused);
      } else {
        s.billing.isPaused = isPaused;
      }
    }
  });
}
export function setProrated(service, isProrated) {
  return produce(service, s => {
    if (serviceBillingModel.isOngoing(s.billing) && isProrated !== s.billing.isProrated) {
      if (isAwaitingApproval(s.status)) {
        return toggleNewOngoingBilling(service, ToggledField.IsProrated);
      } else {
        s.billing.isProrated = isProrated;
      }
    }
  });
}
export function setParticipation(service, optional, includedByDefault) {
  return _objectSpread(_objectSpread({}, service), {}, {
    participation: optional ? makeOptionalParticipation(includedByDefault) : makeMandatoryParticipation()
  });
}
export function setTerminated(service) {
  return _objectSpread(_objectSpread({}, service), {}, {
    status: makeTerminatedStatus(effectiveDate(service) || new Date())
  });
}
export function setCanceled(service) {
  return _objectSpread(_objectSpread({}, service), {}, {
    status: makeCanceledStatus()
  });
}
export function setAwaitingApproval(service, options) {
  return _objectSpread(_objectSpread({}, service), {}, {
    status: makeAwaitingApprovalStatus(options)
  });
}
export function discardChanges(service) {
  if (isAmended(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeApprovedStatus({
        date: effectiveDate(service) || new Date(),
        effectiveDate: startBillingAt(service)
      })
    });
  } else if (isCompleted(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: _objectSpread(_objectSpread({}, service.status), {}, {
        completed: {
          effectiveDate: startBillingAt(service),
          effectiveDateSource: null
        }
      })
    });
  } else {
    return service;
  }
}
export function setChanges(service, changes) {
  if (isAmended(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeAmendedStatus(_objectSpread(_objectSpread({
        date: service.status.date
      }, service.status.amended), {}, {
        changes
      }))
    });
  } else if (isAwaitingApproval(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeAmendedStatus({
        date: effectiveDate(service),
        approvalMethod: service.status.awaitingApproval.approvalMethod,
        amendedOn: service.status.awaitingApproval.addedOn,
        changes,
        note: service.status.awaitingApproval.note
      })
    });
  } else if (isCompleted(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeCompletedStatus(service.status.date, changes)
    });
  } else {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeAmendedStatus({
        changes,
        date: effectiveDate(service)
      })
    });
  }
}
export function setBillingChanges(service, billing) {
  const existingChanges = changes(service);
  const newChanges = existingChanges ? _objectSpread(_objectSpread({}, existingChanges), {}, {
    billing
  }) : makeChanges({
    billing
  });
  return setChanges(service, newChanges);
}
export function setBillingDayOfWeekChanges(service, dayOfWeek) {
  const newBilling = serviceBillingModel.setBillingDayOfWeek(applyChanges(service).billing, dayOfWeek);
  return setBillingChanges(service, newBilling);
}
export function setBillingDayOfMonthChanges(service, dayOfMonth) {
  const newBilling = serviceBillingModel.setBillingDayOfMonth(applyChanges(service).billing, dayOfMonth);
  return setBillingChanges(service, newBilling);
}
export function setBillingDayOfYearChanges(service, dayOfYear) {
  const newBilling = serviceBillingModel.setBillingDayOfYear(applyChanges(service).billing, dayOfYear);
  return setBillingChanges(service, newBilling);
}
export function setIncludedServicesChanges(service, includedServices) {
  const newPrice = includedServices.reduce((acc, service) => acc.plus(service.rate), new Decimal(0));
  const existingChanges = changes(service);
  const initialCost = (existingChanges === null || existingChanges === void 0 ? void 0 : existingChanges.cost) || service.cost;
  const newCost = serviceCostModel.match(initialCost, {
    fixed: cost => _objectSpread(_objectSpread({}, cost), {}, {
      price: newPrice
    }),
    variable: cost => _objectSpread(_objectSpread({}, cost), {}, {
      price: newPrice
    }),
    range: cost => _objectSpread(_objectSpread({}, cost), {}, {
      maxPrice: newPrice
    })
  });
  const newBundle = {
    services: includedServices
  };
  const newChanges = existingChanges ? _objectSpread(_objectSpread({}, existingChanges), {}, {
    bundle: newBundle,
    cost: newCost
  }) : makeChanges({
    bundle: newBundle,
    cost: newCost
  });
  return setChanges(service, newChanges);
}
export function setCost(service, cost) {
  return _objectSpread(_objectSpread({}, service), {}, {
    cost
  });
}
export function setCostChanges(service, cost) {
  const existingChanges = changes(service);
  const newChanges = existingChanges ? _objectSpread(_objectSpread({}, existingChanges), {}, {
    cost
  }) : makeChanges({
    cost
  });
  return setChanges(service, newChanges);
}
export function setPriceIncreaseChanges(service, priceIncrease) {
  const newCost = serviceCostModel.setPriceIncrease(applyChanges(service).cost, priceIncrease);
  return setCostChanges(service, newCost);
}
export function setPreapprovedAmountChanges(service, amount, controlledByAdvancedSettings) {
  const newCost = serviceCostModel.setPreapprovedAmount(applyChanges(service).cost, amount, controlledByAdvancedSettings);
  return setCostChanges(service, newCost);
}
export function setNote(service, note) {
  if (isAmended(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: _objectSpread(_objectSpread({}, service.status), {}, {
        amended: _objectSpread(_objectSpread({}, service.status.amended), {}, {
          note
        })
      })
    });
  } else if (isAwaitingApproval(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: _objectSpread(_objectSpread({}, service.status), {}, {
        awaitingApproval: _objectSpread(_objectSpread({}, service.status.awaitingApproval), {}, {
          note
        })
      })
    });
  }
  return service;
}
export function applyChanges(service) {
  return isAmended(service.status) ? applyChangesOnService(service, service.status.amended.changes) : isCompleted(service.status) ? applyChangesOnService(service, service.status.completed.changes) : service;
}
function applyChangesOnService(service, changes) {
  if (!changes) return service;
  return _objectSpread(_objectSpread({}, service), {}, {
    name: changes.name != null ? changes.name : service.name,
    description: changes.description != null ? changes.description : service.description,
    billing: changes.billing != null ? changes.billing : service.billing,
    cost: changes.cost != null ? changes.cost : service.cost,
    bundle: changes.bundle != null ? changes.bundle : service.bundle
  });
}
export function setPriceIncrease(service, priceIncrease) {
  return _objectSpread(_objectSpread({}, service), {}, {
    cost: serviceCostModel.setPriceIncrease(service.cost, priceIncrease)
  });
}
export function setDiscount(service, discount) {
  return _objectSpread(_objectSpread({}, service), {}, {
    cost: serviceCostModel.setDiscount(service.cost, discount)
  });
}
export function setDiscountChanges(service, discount) {
  const newCost = serviceCostModel.setDiscount(applyChanges(service).cost, discount);
  return setCostChanges(service, newCost);
}
export function setPrice(service, cost) {
  return _objectSpread(_objectSpread({}, service), {}, {
    cost: serviceCostModel.setPrice(service.cost, cost)
  });
}
export function setPriceChanges(service, cost) {
  const newCost = serviceCostModel.setPrice(applyChanges(service).cost, cost);
  return setCostChanges(service, newCost);
}
export function setFinancialCentsIntegration(service, financialCents) {
  const integrations = service.integrations.setFinancialCentsIntegration(financialCents);
  return _objectSpread(_objectSpread({}, service), {}, {
    integrations
  });
}
export function setKarbonIntegration(service, karbon) {
  const integrations = service.integrations.setKarbonIntegration(karbon);
  return _objectSpread(_objectSpread({}, service), {}, {
    integrations
  });
}
export function setLimitedTimeDiscountOffer(service, limitedTimeDiscountOffer) {
  if (isAwaitingApproval(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: _objectSpread(_objectSpread({}, service.status), {}, {
        awaitingApproval: _objectSpread(_objectSpread({}, service.status.awaitingApproval), {}, {
          limitedTimeDiscountOffer: limitedTimeDiscountOffer
        })
      })
    });
  }
  return service;
}
export function setOnApproval(service) {
  return _objectSpread(_objectSpread({}, service), {}, {
    billing: serviceBillingModel.makeOnApprovalBilling(),
    status: makeOnApprovalStatus(service)
  });
}
export function setOneTimeInStages(service, oneTimeInStages) {
  return _objectSpread(_objectSpread({}, service), {}, {
    billing: serviceBillingModel.makeOneTimeInStagesBilling(oneTimeInStages),
    status: makeOneTimeInStagesStatus(service)
  });
}
export function setOneTimeManual(service) {
  return _objectSpread(_objectSpread({}, service), {}, {
    status: makeOneTimeManualStatus(service),
    billing: serviceBillingModel.makeOneTimeManualBilling()
  });
}
export function setOngoing(service) {
  return _objectSpread(_objectSpread({}, service), {}, {
    billing: serviceBillingModel.makeOngoingBilling(serviceBillingModel.recurrencePeriod(service.billing) || RecurrencePeriodTypes.Monthly, {
      isAwaitingApproval: isAwaitingApproval(service.status),
      billingDayOfMonth: serviceBillingModel.billingDayOfMonth(service.billing),
      billingDayOfWeek: serviceBillingModel.billingDayOfWeek(service.billing),
      billingDayOfYear: serviceBillingModel.billingDayOfYear(service.billing),
      isBilledUpfront: serviceBillingModel.isBilledUpfront(service.billing),
      maxCharges: serviceBillingModel.maxCharges(service.billing),
      billUpfrontState: makeBillUpfrontState(true)
    })
  });
}
export function setRepeatableManual(service) {
  return _objectSpread(_objectSpread({}, service), {}, {
    billing: serviceBillingModel.makeRepeatableManualBilling(serviceBillingModel.recurrencePeriod(service.billing) || RecurrencePeriodTypes.Monthly, {
      billingDayOfMonth: serviceBillingModel.billingDayOfMonth(service.billing),
      billingDayOfWeek: serviceBillingModel.billingDayOfWeek(service.billing),
      billingDayOfYear: serviceBillingModel.billingDayOfYear(service.billing),
      isBilledUpfront: serviceBillingModel.isBilledUpfront(service.billing),
      maxCharges: serviceBillingModel.maxCharges(service.billing)
    }),
    status: serviceModel.makeRepeatableManualStatus(service)
  });
}
export function setDiscountPeriodExpiration(service, discountPeriodExpiration) {
  let discount = service.cost.discount;
  if (discount) {
    discount = discount.setExpiration(discountPeriodExpiration);
  }
  if (isAwaitingApproval(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      cost: serviceCostModel.setDiscount(service.cost, discount)
    });
  }
  return setDiscountChanges(service, discount);
}
export function setPreapprovedAmount(service, amount, controlledByAdvancedSettings) {
  return _objectSpread(_objectSpread({}, service), {}, {
    cost: serviceCostModel.setPreapprovedAmount(service.cost, amount, controlledByAdvancedSettings)
  });
}
export const setStartBillingAt = (service, startBillingAt) => {
  return toggleNewOngoingBilling(service, ToggledField.StartBillingAt, {
    effectiveDate: startBillingAt !== null && startBillingAt !== void 0 ? startBillingAt : undefined
  });
};
export function setIncludedServices(service, includedServices) {
  const newPrice = includedServices.reduce((acc, service) => acc.plus(service.rate), new Decimal(0));
  return _objectSpread(_objectSpread({}, service), {}, {
    cost: serviceCostModel.match(service.cost, {
      fixed: cost => _objectSpread(_objectSpread({}, cost), {}, {
        price: newPrice
      }),
      variable: cost => _objectSpread(_objectSpread({}, cost), {}, {
        price: newPrice
      }),
      range: cost => cost
    }),
    bundle: {
      services: includedServices
    }
  });
}
export function setBundlePrice(service, includedServiceRates, bundleRate, discountAmount) {
  if (isAwaitingApproval(service.status)) {
    var _service$cost$discoun;
    const newDiscount = discountAmount ? DiscountModel.makePercent(discountAmount, (_service$cost$discoun = service.cost.discount) === null || _service$cost$discoun === void 0 ? void 0 : _service$cost$discoun.expiration()) : null;
    const newCost = serviceCostModel.setPrice2(serviceCostModel.setDiscount(service.cost, newDiscount), bundleRate);
    const newService = setCost(service, newCost);
    if (!newService.bundle) {
      return newService;
    }
    const newIncludedServices = applyNewIncludedServiceRates(newService.bundle.services, includedServiceRates);
    return setIncludedServices(newService, newIncludedServices);
  } else {
    var _serviceWithChanges$c;
    const serviceWithChanges = applyChanges(service);
    const newDiscount = discountAmount ? DiscountModel.makePercent(discountAmount, (_serviceWithChanges$c = serviceWithChanges.cost.discount) === null || _serviceWithChanges$c === void 0 ? void 0 : _serviceWithChanges$c.expiration()) : null;
    const newCost = serviceCostModel.setPrice2(serviceCostModel.setDiscount(serviceWithChanges.cost, newDiscount), bundleRate);
    const newService = setCostChanges(service, newCost);
    if (!newService.bundle) {
      return newService;
    }
    const newIncludedServices = applyNewIncludedServiceRates(newService.bundle.services, includedServiceRates);
    return setIncludedServicesChanges(newService, newIncludedServices);
  }
}
function applyNewIncludedServiceRates(services, includedServiceRates) {
  return services.map(service => {
    const rate = includedServiceRates[service.serviceTemplateId || ''];
    return _objectSpread(_objectSpread({}, service), {}, {
      rate: rate >= 0 ? new Decimal(rate) : service.rate
    });
  });
}